import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FileText, 
  Camera, 
  Scale, 
  User, 
  Truck, 
  MapPin, 
  Clock, 
  Download,
  Printer,
  Eye
} from "lucide-react";

interface TransactionDetailProps {
  transaction: {
    id: number;
    transactionNo: string;
    date: string;
    time: string;
    operator: string;
    orderNo: string;
    transactionType: string;
    vehicleReg: string;
    trailer1PlateNumber?: string;
    trailer2PlateNumber?: string;
    driverName?: string;
    driverId?: string;
    tareWeight: number;
    grossWeight: number;
    netWeight: number;
    unit: string;
    customerName?: string;
    freightCompany?: string;
    transporterName?: string;
    weighbridgeName?: string;
    entryTime?: string;
    exitTime?: string;
    startedAt?: string;
    endedAt?: string;
    duration?: string;
    status?: string;
    visitStatus?: string;
    kanbanStatus?: string;
    accessDecision?: string;
    supplierExternalId?: string;
    weighbridgeEntryWeight?: number;
    weighbridgeExitWeight?: number;
    deliveryPointEntryWeight?: number;
    deliveryPointExitWeight?: number;
  };
}

export default function TransactionDetailView({ transaction }: TransactionDetailProps) {
  // Calculate hours in lot
  const calculateHoursInLot = () => {
    if (transaction.exitTime && transaction.entryTime) {
      const hours = (new Date(transaction.exitTime).getTime() - new Date(transaction.entryTime).getTime()) / (1000 * 60 * 60);
      return Math.round(hours * 10) / 10;
    } else if (transaction.entryTime) {
      const hours = (new Date().getTime() - new Date(transaction.entryTime).getTime()) / (1000 * 60 * 60);
      return Math.round(hours * 10) / 10;
    }
    return 0;
  };

  const hoursInLot = calculateHoursInLot();

  const generatePDF = () => {
    const content = `
WEIGHBRIDGE TICKET PROCESS - ${transaction.transactionNo}
================================================================

Ticket Number: ${transaction.transactionNo}
Date & Time In: ${transaction.entryTime ? new Date(transaction.entryTime).toLocaleString() : `${transaction.date} ${transaction.time}`}
Hours in Lot: ${hoursInLot > 0 ? hoursInLot.toFixed(1) : 'N/A'}
Status: ${transaction.status || 'Active'}

BASIC INFORMATION
-----------------
Control Plate Number: ${transaction.vehicleReg}
Part Number: ${transaction.orderNo || 'N/A'}

LICENSE PLATE
-------------
License Plate: ${transaction.vehicleReg}
Trailer 1: ${transaction.trailer1PlateNumber || 'N/A'}
Trailer 2: ${transaction.trailer2PlateNumber || 'N/A'}

ADDITIONAL INFORMATION
----------------------
Reference: ${transaction.orderNo || 'N/A'}
Driver Name: ${transaction.driverName || 'N/A'}
Driver Status: ${transaction.driverId ? 'Verified' : 'Unknown'}

FREIGHT COMPANY
---------------
Company: ${transaction.freightCompany || transaction.customerName || 'N/A'}
Transporter: ${transaction.transporterName || 'N/A'}

WEIGHT MEASUREMENTS
-------------------
Tare Weight: ${transaction.tareWeight ? transaction.tareWeight.toLocaleString() : 'N/A'} ${transaction.unit}
Gross Weight: ${transaction.grossWeight ? transaction.grossWeight.toLocaleString() : 'N/A'} ${transaction.unit}
Net Weight: ${transaction.netWeight ? transaction.netWeight.toLocaleString() : 'N/A'} ${transaction.unit}

Generated by Weigh8 Weighbridge Management System
Report Generated: ${new Date().toLocaleString()}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Weighbridge_Ticket_${transaction.transactionNo}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div>
          <h1 className="text-xl font-bold text-blue-600" data-testid="text-title">
            Transaction Details - {transaction.transactionNo}
          </h1>
        </div>
        <div className="flex space-x-2">
          <Button onClick={generatePDF} variant="outline" size="sm" data-testid="button-export">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => window.print()} variant="outline" size="sm" data-testid="button-print">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Simplified Layout - Single Column */}
      <div className="space-y-6">
        {/* Essential Information Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Transaction Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Date */}
              <div>
                <label className="text-sm font-medium text-gray-600">Date</label>
                <div className="text-base font-medium" data-testid="text-date">
                  {transaction.startedAt ? new Date(transaction.startedAt).toLocaleDateString() : (transaction.entryTime ? new Date(transaction.entryTime).toLocaleDateString() : transaction.date)}
                </div>
                <div className="text-sm text-gray-500">
                  {transaction.endedAt ? new Date(transaction.endedAt).toLocaleTimeString() : (transaction.entryTime ? new Date(transaction.entryTime).toLocaleTimeString() : transaction.time)}
                </div>
              </div>

              {/* Order Number */}
              <div>
                <label className="text-sm font-medium text-gray-600">Order Number</label>
                <div className="text-base font-mono font-medium" data-testid="text-order-number">
                  {transaction.orderNo || 'N/A'}
                </div>
              </div>

              {/* Transporter */}
              <div>
                <label className="text-sm font-medium text-gray-600">Transporter</label>
                <div className="text-base font-medium" data-testid="text-transporter">
                  {transaction.transporterName || 'N/A'}
                </div>
              </div>

              {/* Number Plate */}
              <div>
                <label className="text-sm font-medium text-gray-600">Number Plate</label>
                <div className="text-lg font-bold" data-testid="text-plate-number">
                  {transaction.vehicleReg}
                </div>
                {transaction.trailer1PlateNumber && (
                  <div className="text-sm text-gray-500">T1: {transaction.trailer1PlateNumber}</div>
                )}
                {transaction.trailer2PlateNumber && (
                  <div className="text-sm text-gray-500">T2: {transaction.trailer2PlateNumber}</div>
                )}
              </div>

              {/* Driver Fullname */}
              <div>
                <label className="text-sm font-medium text-gray-600">Driver Fullname</label>
                <div className="text-base font-medium" data-testid="text-driver-name">
                  {transaction.driverName || 'N/A'}
                </div>
              </div>

              {/* ID */}
              <div>
                <label className="text-sm font-medium text-gray-600">ID</label>
                <div className="text-base font-mono font-medium" data-testid="text-driver-id">
                  {transaction.driverId || 'N/A'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Weight Measurements Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center">
              <Scale className="w-5 h-5 mr-2" />
              Weight Measurements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Tare */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <label className="text-sm font-medium text-gray-600">Tare</label>
                <div className="text-xl font-bold text-blue-600" data-testid="text-tare-weight">
                  {transaction.tareWeight ? `${transaction.tareWeight.toLocaleString()} ${transaction.unit}` : 'N/A'}
                </div>
              </div>

              {/* Gross */}
              <div className="p-4 bg-orange-50 rounded-lg">
                <label className="text-sm font-medium text-gray-600">Gross</label>
                <div className="text-xl font-bold text-orange-600" data-testid="text-gross-weight">
                  {transaction.grossWeight ? `${transaction.grossWeight.toLocaleString()} ${transaction.unit}` : 'N/A'}
                </div>
              </div>

              {/* Net */}
              <div className="p-4 bg-green-50 rounded-lg">
                <label className="text-sm font-medium text-gray-600">Net</label>
                <div className="text-xl font-bold text-green-600" data-testid="text-net-weight">
                  {transaction.netWeight ? `${transaction.netWeight.toLocaleString()} ${transaction.unit}` : 'N/A'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}